import { NgIf, TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbToastrService
} from '@nebular/theme';
import { Subject, takeUntil } from 'rxjs';
import { BatchJobService } from 'src/app/core/services/batch-job.service';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { InsightWebSocketService } from 'src/app/core/services/web-socket.service';
import { Insight } from 'src/app/models/insight';
import { CONFIG_REGEX, STATUSES } from 'src/app/shared/constants/general-constants';
import { CustomValueHandlingPipe } from 'src/app/shared/pipes/custom-value-handling.pipe';
import { FormatTimePipe } from 'src/app/shared/pipes/format-time.pipe';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { StatsInsightComponent } from '../stats-insight/stats-insight.component';
import { InsightStatusHandlerComponent } from './insight-status-handler/insight-status-handler.component';

@Component({
  selector: 'app-view-insight',
  templateUrl: './view-insight.component.html',
  styleUrls: ['./view-insight.component.css'],
  providers: [CustomValueHandlingPipe],
  imports: [
    NbCardModule,
    NbIconModule,
    NbButtonModule,
    NgIf,
    NbCheckboxModule,
    CodemirrorModule,
    FormsModule,
    TitleCasePipe,
    CustomValueHandlingPipe,
    FormatTimePipe
  ]
})
export class ViewInsightComponent implements OnInit {
  public insight!: Insight;
  public statusLabel = '-';
  public scheduleLabel = '-';
  public frequencyLabel = '-';
  public averageExecutionDuration = 0;
  public insight_name_validator = false;
  public displayManagementButtons = false;
  private unsubscribe = new Subject<void>();

  constructor(
    private utilityService: UtilityService,
    protected ref: NbDialogRef<ViewInsightComponent>,
    private customValueHandlingPipe: CustomValueHandlingPipe,
    private restrictionService: OktaRestriction,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService,
    private socketService: InsightWebSocketService,
    private batchJobService: BatchJobService
  ) {}

  public regexValidator(nameRe: RegExp, value: string) {
    const validation = nameRe.test(value);

    return validation ? false : true;
  }

  public ngOnInit() {
    const regex = String.raw`${CONFIG_REGEX}`;
    this.insight_name_validator = this.regexValidator(new RegExp(regex, 'i'), this.insight.name);
    this.statusLabel = this.insight.enabled ? $localize`Active` : $localize`Inactive`;
    if (this.insight.schedule_seconds) {
      this.frequencyLabel = this.utilityService.getMatchedFrequencyLabel(this.insight.schedule_seconds) || '-';
    }
    if (this.insight.start_date && this.insight.end_date) {
      this.scheduleLabel =
        this.customValueHandlingPipe.transform(this.insight.start_date, 'start_date') +
        ' - ' +
        this.customValueHandlingPipe.transform(this.insight.end_date, 'end_date');
    }
    if (this.insight.total_duration && this.insight.executions) {
      this.averageExecutionDuration = this.insight.total_duration / this.insight.executions;
    }
    this.displayManagementButtons = this.restrictionService.manager_permission;
  }

  public close() {
    this.ref.close();
  }

  public runOnce() {
    if (this.insight.status === STATUSES.RUNNING) {
      this.toastrService.show(status, $localize`Insight is already running`, { status: 'danger' });
    } else {
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Starting Insight: ${this.insight.id}`,
            message: $localize`Are you sure you want to start this insight?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.socketService.setUp();
            this.batchJobService
              .runInsight(this.insight)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((data) => {
                setTimeout(() => {
                  this.socketService.sendMessage(data.job_id, this.insight.id);
                }, 2000);
              });
          }
        });
    }
  }

  public viewStats() {
    this.dialogService.open(StatsInsightComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        insight: this.insight
      }
    });
  }

  public updateStatus(action: string) {
    this.dialogService
      .open(InsightStatusHandlerComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: action,
          insight: Insight.copy(this.insight) as Insight
        }
      })
      .onClose.subscribe(() => {
        this.close();
      });
  }

  public toDateTime = (epoch_time: number) => {
    const t = new Date(1970, 0, 1); // Epoch
    t.setSeconds(epoch_time);

    return t;
  };
}
